// src/App.jsx
import React, { Suspense, useRef, useMemo, useEffect, useState } from 'react';
import { Canvas, extend, useFrame, useLoader, useThree } from '@react-three/fiber';
import { OrbitControls, useGLTF, useTexture, Stars } from '@react-three/drei';
import * as THREE from 'three';
import { Water } from 'three/examples/jsm/objects/Water.js';
import HeroSection from './components/HeroSection';
import MarineDilemmaSection from './components/MarineDilemmaSection'; // Import the new section
import ModelShowcaseSection from './components/ModelShowcaseSection';
import SceneViewer from './scene_studio/viewer/SceneViewer'; // <-- IMPORT SceneViewer
import waterFlowSceneConfig from './scene_studio/configs/water_flow_simulation_scene_config.json'; // <-- IMPORT Scene Config
import InvestorOfferSection from './components/InvestorOfferSection';
// import MarketSection from './components/MarketSection'; // Remove the import for MarketSection
import Market2Section from './components/Market2Section'; // Keep this import
// import WhoWeAreSection from './components/WhoWeAreSection'; // Commented out WhoWeAreSection
import ContactSection from './components/ContactSection';
import Footer from './components/Footer'; 
import useWaterStore from './store/waterStore'; // Corrected import path
import useCameraStore from './store/cameraStore'; // Import camera store
import useSkyStore from './store/skyStore'; // Import sky store
import { vertexShader, fragmentShader } from './shaders/waterShader'; // Import shaders
import { vertexShader as skyVertexShader, fragmentShader as skyFragmentShader } from './shaders/skyShader'; // Import sky shaders (renamed)
// Removed Radix Accordion import
// import * as Accordion from '@radix-ui/react-accordion'; 
import { KeyRound } from 'lucide-react'; // Import KeyRound icon
import LanguageSwitcher from './components/LanguageSwitcher'; // Import switcher
import { useTranslation } from 'react-i18next'; // Import hook
import useModelStore from './store/modelStore'; // Import model store
import ModelComponent from './components/ModelComponent'; // Import extracted component
import { useLocation, Routes, Route } from 'react-router-dom'; // Import Routes, Route
import ImprintPage from './components/ImprintPage'; // Import the new page
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import ThankYouPage from './components/ThankYouPage';
import ScrollIndicator from './components/ScrollIndicator'; // Import Scroll Indicator
import UsecaseSection from './components/UsecaseSection'; // Import the new UsecaseSection
import AnimatedWaves from './components/AnimatedWaves'; // <-- IMPORT THE NEW COMPONENT
import FloatingFAQButton from './components/FloatingFAQButton';
import FAQModal from './components/FAQModal';
import OnePager from './components/OnePager';
import { ProductList } from './components/shop/ProductList';
import { ProductDetail } from './components/shop/ProductDetail';
import { Cart } from './components/shop/Cart';

// Extend Water to make it available as a JSX element
extend({ Water })

// New component using ShaderMaterial
function ShaderToyWater() {
  const meshRef = useRef();
  // const size = useThree((state) => state.size); // Size is now read in useFrame
  const storeState = useWaterStore(); // Get the whole store state
  const skyState = useSkyStore.getState(); // Also get the initial sky state

  // Memoize uniforms object - Create only ONCE
  const uniforms = useMemo(() => ({
    u_time: { value: 0.0 },
    // Initialize resolution, will be updated in useFrame
    u_resolution: { value: new THREE.Vector2() }, 
    uSeaHeight: { value: storeState.seaHeight },
    uSeaChoppy: { value: storeState.seaChoppy },
    uSeaSpeed: { value: storeState.seaSpeed },
    uSeaFreq: { value: storeState.seaFreq },
    uSeaBaseColor: { value: new THREE.Color(storeState.seaBaseColor) },
    uSeaWaterColor: { value: new THREE.Color(storeState.seaWaterColor) },
    uSkyIntensity: { value: storeState.skyIntensity },
    // Empty dependency array: uniforms object reference is stable
  }), []); 

  // Update uniforms imperatively in useFrame
  useFrame((state, delta) => {
    if (meshRef.current) {
      const currentStoreState = useWaterStore.getState();
      const currentSkyState = useSkyStore.getState();
      const material = meshRef.current.material;
      
      // Update time
      material.uniforms.u_time.value += delta;
      
      // Update resolution based on current canvas size
      material.uniforms.u_resolution.value.set(state.size.width, state.size.height);
      
      // Update shader parameters from the store state
      material.uniforms.uSeaHeight.value = currentStoreState.seaHeight;
      material.uniforms.uSeaChoppy.value = currentStoreState.seaChoppy;
      material.uniforms.uSeaSpeed.value = currentStoreState.seaSpeed;
      material.uniforms.uSeaFreq.value = currentStoreState.seaFreq;
      material.uniforms.uSeaBaseColor.value.set(currentStoreState.seaBaseColor);
      material.uniforms.uSeaWaterColor.value.set(currentStoreState.seaWaterColor);
      
      // Adjust sky intensity based on night/day mode
      // In day mode, we want more sky reflection on the water
      const baseSkyIntensity = currentStoreState.skyIntensity;
      const finalSkyIntensity = currentSkyState.isNight ? baseSkyIntensity : baseSkyIntensity * 1.5;
      material.uniforms.uSkyIntensity.value = finalSkyIntensity;
    }
  });

  return (
    <mesh ref={meshRef} rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]}>
      <planeGeometry args={[500, 500, 1, 1]} />
      <shaderMaterial
        uniforms={uniforms}
        fragmentShader={fragmentShader}
        vertexShader={vertexShader}
        side={THREE.DoubleSide}
      />
    </mesh>
  );
}

// New component to update camera and controls based on store
function CameraUpdater() {
  const { camera, controls } = useThree();
  const { fov, minDistance, maxDistance } = useCameraStore();

  useEffect(() => {
    if (camera) {
      camera.fov = fov;
      camera.updateProjectionMatrix(); // Important: update matrix after changing fov
    }
  }, [camera, fov]);

  useEffect(() => {
    if (controls) {
      controls.minDistance = minDistance;
      controls.maxDistance = maxDistance;
    }
  }, [controls, minDistance, maxDistance]);

  return null; // This component doesn't render anything visible
}

// New Sky Component (renamed from SparklingStars)
function SkyBackground({ particleCount = 500000 }) {
  const pointsRef = useRef();
  const initialSkyParams = useSkyStore.getState(); 

  // Detect mobile for additional optimizations
  const isMobile = useMemo(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }, []);

  // Create star positions and colors
  const [positions, colors] = useMemo(() => {
    const count = particleCount; // Use the prop instead of hardcoded value
    const radius = isMobile ? 100 : 150; // Smaller radius on mobile
    const positions = new Float32Array(count * 3);
    const colors = new Float32Array(count * 3);
    const color = new THREE.Color();
    
    console.log(`SkyBackground: Creating ${count} particles for the sky`);
    
    for (let i = 0; i < count; i++) {
      const r = radius * Math.cbrt(Math.random());
      const theta = Math.random() * 2 * Math.PI;
      const phi = Math.acos(2 * Math.random() - 1);
      const x = r * Math.sin(phi) * Math.cos(theta);
      const y = r * Math.sin(phi) * Math.sin(theta);
      const z = r * Math.cos(phi);
      positions.set([x, y, z], i * 3);
      color.setHSL(Math.random() * 0.1 + 0.6, 0.2, Math.random() * 0.3 + 0.7);
      colors.set([color.r, color.g, color.b], i * 3);
    }
    return [positions, colors];
  }, [particleCount, isMobile]); // Recreate when particleCount or mobile status changes

  // Memoize uniforms - Create only ONCE
  const uniforms = useMemo(() => ({
    u_time: { value: 0.0 },
    // Use initial state values here
    u_base_size: { value: initialSkyParams.baseSize }, 
    u_size_attenuation_factor: { value: initialSkyParams.factor },
    u_sparkle_speed: { value: initialSkyParams.sparkleSpeed },
    u_sparkle_intensity: { value: initialSkyParams.sparkleIntensity },
    u_fade: { value: initialSkyParams.fade },
    u_density: { value: initialSkyParams.density },
    u_is_night: { value: initialSkyParams.isNight }, // Add night mode uniform
    // Empty dependency array: uniforms object reference is stable
  }), []); 

  // Update uniforms in useFrame
  useFrame((state, delta) => {
    if (pointsRef.current) {
      const currentParams = useSkyStore.getState();
      const material = pointsRef.current.material;
      material.uniforms.u_time.value += delta;
      material.uniforms.u_base_size.value = currentParams.baseSize;
      material.uniforms.u_size_attenuation_factor.value = currentParams.factor;
      material.uniforms.u_sparkle_speed.value = currentParams.sparkleSpeed;
      material.uniforms.u_sparkle_intensity.value = currentParams.sparkleIntensity;
      material.uniforms.u_fade.value = currentParams.fade;
      material.uniforms.u_density.value = currentParams.density;
      material.uniforms.u_is_night.value = currentParams.isNight; // Update night mode
    }
  });

  return (
    <points ref={pointsRef}>
      <bufferGeometry attach="geometry">
        <bufferAttribute
          attach="attributes-position"
          array={positions}
          count={positions.length / 3}
          itemSize={3}
        />
        <bufferAttribute
          attach="attributes-color"
          array={colors}
          count={colors.length / 3}
          itemSize={3}
          normalized 
        />
      </bufferGeometry>
      <shaderMaterial
        attach="material"
        uniforms={uniforms}
        vertexShader={skyVertexShader}
        fragmentShader={skyFragmentShader}
        blending={THREE.AdditiveBlending} // Nice effect for stars
        depthWrite={false} // Prevents stars obscuring each other weirdly
        vertexColors // Use the color attribute
        transparent // Needed for fading/blending
      />
    </points>
  );
}

// New component for the sky dome with day/night backgrounds
function SkyDome() {
  const { isNight, dayTopColor, dayHorizonColor } = useSkyStore();
  const domeRef = useRef();

  // Generate colors based on store values
  const colors = useMemo(() => {
    // Use the initial store values
    const initial = useSkyStore.getState();
    return getSkyColors(400, 32, 32, initial.dayTopColor, initial.dayHorizonColor);
  }, []);

  // Update the material visibility based on the isNight state
  useFrame(() => {
    if (domeRef.current) {
      const currentState = useSkyStore.getState();
      domeRef.current.material.visible = !currentState.isNight;
      
      // Update colors when day sky colors change
      if (!currentState.isNight) {
        const colorAttribute = domeRef.current.geometry.attributes.color;
        if (colorAttribute) {
          const newColors = getSkyColors(400, 32, 32, currentState.dayTopColor, currentState.dayHorizonColor);
          colorAttribute.array.set(newColors);
          colorAttribute.needsUpdate = true;
        }
      }
    }
  });

  return (
    <mesh ref={domeRef} renderOrder={-1}>
      <sphereGeometry args={[400, 32, 32]}>
        <bufferAttribute 
          attach="attributes-color"
          array={colors}
          count={colors.length / 3}
          itemSize={3}
          normalized
        />
      </sphereGeometry>
      <meshBasicMaterial 
        color="white" 
        side={THREE.BackSide} 
        vertexColors={true}
        visible={!isNight}
      />
    </mesh>
  );
}

// Helper function to generate gradient sky colors
function getSkyColors(radius, widthSegments, heightSegments, topColorHex, horizonColorHex) {
  // Calculate the number of vertices in a SphereGeometry
  const vertices = (widthSegments + 1) * (heightSegments + 1);
  const colors = new Float32Array(vertices * 3);
  
  // Set colors based on position
  let colorIndex = 0;
  const topColor = new THREE.Color(topColorHex); // Top color from parameters
  const horizonColor = new THREE.Color(horizonColorHex); // Horizon color from parameters
  const bottomColor = new THREE.Color().copy(horizonColor).multiplyScalar(0.8); // Slightly darker horizon for bottom
  
  // Loop through all vertices
  for (let y = 0; y <= heightSegments; y++) {
    // Calculate normalized height (0 = bottom, 0.5 = middle, 1 = top)
    const v = y / heightSegments;
    let color = new THREE.Color();
    
    // Modified color distribution to make top color more prominent
    // Start transition to top color earlier at 0.3 instead of 0.5
    if (v < 0.3) {
      // Bottom 30% - blend between bottom and horizon
      color.copy(bottomColor).lerp(horizonColor, v / 0.3);
    } else {
      // Top 70% - blend between horizon and top
      // Use a power curve to make the top color show up more distinctly
      const t = Math.pow((v - 0.3) / 0.7, 0.7); // Adjust power for gradient distribution
      color.copy(horizonColor).lerp(topColor, t);
    }
    
    for (let x = 0; x <= widthSegments; x++) {
      colors[colorIndex] = color.r;
      colors[colorIndex + 1] = color.g;
      colors[colorIndex + 2] = color.b;
      colorIndex += 3;
    }
  }
  
  return colors;
}

function ScrollToHash() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace('#', ''));
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }, [hash]);
  return null;
}

function App() {
  const { t } = useTranslation();
  const location = useLocation();
  const { fov, minDistance, maxDistance } = useCameraStore(); 
  const skyParams = useSkyStore();
  const modelTransform = useModelStore(); 
  const [isFAQModalOpen, setIsFAQModalOpen] = useState(false);

  // Add debug logging for component mount
  useEffect(() => {
    console.log('[Debug] App component mounted');
    console.log('[Debug] Current DOM state:', {
      loaderExists: !!document.getElementById('loader'),
      canvasExists: !!document.getElementById('bg-canvas'),
      bodyChildren: document.body.children.length
    });
  }, []);

  // Enhanced loader removal logging
  useEffect(() => {
    try {
      console.log('[Loader Debug] App mounting, attempting to remove loader...');
      const loaderElement = document.getElementById('loader');
      console.log('[Loader Debug] Loader element found:', loaderElement);
      
      if (loaderElement) {
        console.log('[Loader Debug] Adding fade-out class to loader...');
        loaderElement.classList.add('fade-out');
        
        const handleTransitionEnd = () => {
          try {
            console.log('[Loader Debug] Transition ended');
            console.log('[Loader Debug] Loader still exists in DOM:', document.body.contains(loaderElement));
            
            if (document.body.contains(loaderElement)) {
              console.log('[Loader Debug] Removing loader element...');
              loaderElement.remove();
              console.log('[Loader Debug] Loader element removed successfully');
            } else {
              console.log('[Loader Debug] Loader element no longer in DOM, skipping removal');
            }
          } catch (error) {
            console.error('[Loader Debug] Error in transition handler:', error);
          }
        };
        
        console.log('[Loader Debug] Adding transitionend event listener');
        loaderElement.addEventListener('transitionend', handleTransitionEnd);
        
        return () => {
          try {
            console.log('[Loader Debug] Cleanup: removing transition listener');
            loaderElement.removeEventListener('transitionend', handleTransitionEnd);
          } catch (error) {
            console.error('[Loader Debug] Error in cleanup:', error);
          }
        };
      } else {
        console.log('[Loader Debug] No loader element found in DOM');
      }
    } catch (error) {
      console.error('[Loader Debug] Error in loader effect:', error);
    }
  }, []);

  // Add debug logging for route changes
  useEffect(() => {
    console.log('[Debug] Route changed to:', location.pathname);
    console.log('[Debug] Current DOM state after route change:', {
      loaderExists: !!document.getElementById('loader'),
      canvasExists: !!document.getElementById('bg-canvas'),
      bodyChildren: document.body.children.length
    });
  }, [location.pathname]);

  // Preload videos
  useEffect(() => {
    const preloadVideos = async () => {
      const videos = [
        { src: '/videos/waterflow.mp4', name: 'waterflow.mp4' }
      ];

      for (const video of videos) {
        try {
          console.log(`App: Starting video preload for ${video.name}`);
          const response = await fetch(video.src, {
            method: 'HEAD',
            headers: {
              'Accept': 'video/mp4'
            }
          });
          
          if (response.ok) {
            console.log(`App: Video preload successful for ${video.name}, file size:`, response.headers.get('content-length'));
          } else {
            console.warn(`App: Video preload failed for ${video.name} with status:`, response.status);
          }
        } catch (error) {
          console.error(`App: Video preload error for ${video.name}:`, error);
        }
      }
    };
    
    preloadVideos();
  }, []);

  // Add useEffect to add/remove 'onepager-body' class based on route
  useEffect(() => {
    if (location.pathname === '/joinus') {
      document.body.classList.add('onepager-body');
    } else {
      document.body.classList.remove('onepager-body');
    }
  }, [location.pathname]);

  // Add mobile detection
  const isMobile = useMemo(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
  }, []);

  // == Conditional Rendering based on path ==

  // --- /screenshot route: Only Ocean/Sky --- 
  if (location.pathname === '/screenshot') {
    // Mobile detection
    const isMobile = useMemo(() => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    }, []);
    
    // Optimize settings for mobile
    const dpr = isMobile ? [0.6, 1] : [1, 2]; // Lower resolution on mobile
    const particleCount = isMobile ? 10000 : 250000; // Dramatically reduce particles for screenshot route
    
    return (
      <Canvas 
        camera={{ position: [0, 2, 12], fov: fov }} 
        style={{ position: 'fixed', top: 0, left: 0, zIndex: 0 }} 
        gl={{ 
          toneMapping: THREE.ACESFilmicToneMapping, 
          antialias: !isMobile,
          powerPreference: "high-performance",
          precision: isMobile ? "lowp" : "highp",
        }}
        dpr={dpr}
        performance={{ min: 0.1 }} // Set minimum frame rate
      >
        {/* Lights suitable for background? Maybe just ambient */}
        <ambientLight intensity={0.8} />
        {/* Sky and Water */} 
        <SkyDome />
        <SkyBackground particleCount={particleCount} />
        <Suspense fallback={null}> 
          <ShaderToyWater /> 
        </Suspense>
        {/* No OrbitControls needed for static screenshot */}
      </Canvas>
      // Optionally add a simple message: "Screenshot Mode - Ocean/Sky View"
      // <div style={{position: 'fixed', top: 10, left: 10, color: 'white', zIndex: 10}}>Screenshot Mode - Ocean/Sky View</div>
    );
  }

  // --- /productshot route: Only Model --- 
  else if (location.pathname === '/productshot') {
    // Mobile detection
    const isMobile = useMemo(() => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    }, []);
    
    // Optimize settings for mobile
    const dpr = isMobile ? 1 : 2; // Lower DPR on mobile
    
    return (
      <Canvas 
        camera={{ position: [0, 1, 5], fov: 50 }} // Closer camera, slightly different FOV
        style={{ position: 'fixed', top: 0, left: 0, zIndex: 0, background: '#202020' }} // Plain background
        gl={{ 
          toneMapping: THREE.ACESFilmicToneMapping, 
          antialias: !isMobile, // Disable antialiasing on mobile
          alpha: false,
          powerPreference: "high-performance",
          precision: isMobile ? "lowp" : "highp", // Lower precision on mobile
        }} 
        dpr={dpr}
        performance={{ min: 0.1 }} // Set minimum frame rate
      >
        {/* Simple lighting for the model */}
        <ambientLight intensity={1.0} />
        <directionalLight intensity={2.5} position={[5, 5, 5]} />
        {/* Only include the second light on non-mobile devices */}
        {!isMobile && <directionalLight intensity={1.5} position={[-5, 5, -5]} />}
        
        <Suspense fallback={<div style={{ color: 'white', position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }}>Loading 3D Model...</div>}> 
          {/* Only the ModelComponent */}
          <ModelComponent 
            position={[modelTransform.posX, modelTransform.posY, modelTransform.posZ]}
            scale={modelTransform.scale * 3} // Apply 3x scale multiplier
            rotation={[modelTransform.rotX, modelTransform.rotY, modelTransform.rotZ]}
          /> 
        </Suspense>

        {/* OrbitControls with optimizations for mobile */}
        <OrbitControls 
          enableZoom={true}
          enablePan={true} // Allow panning for positioning
          minDistance={1} 
          maxDistance={20}
          enableDamping={!isMobile} // Disable damping on mobile
          rotateSpeed={isMobile ? 0.5 : 1} // Slower rotation on mobile
        />
        <CameraUpdater /> {/* Still useful for zoom limits if needed */}
      </Canvas>
      // Optionally add a message: "Product Shot Mode - Model View"
      // <div style={{position: 'fixed', top: 10, left: 10, color: 'white', zIndex: 10}}>Product Shot Mode - Model View</div>
    );
  }

  // --- Main Application Layout (default path) --- 
  else {
    // Mobile detection
    const isMobile = useMemo(() => {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    }, []);
    
    // Optimize settings for mobile
    const dpr = isMobile ? [0.6, 1] : [1, 2]; // Lower resolution on mobile
    const particleCount = isMobile ? 20000 : 500000; // Significantly reduce particles on mobile to prevent crashes
    
    return (
      <>
        <ScrollToHash />
        <div id="app-container" style={{ 
          position: 'relative', 
          width: '100%', 
          height: '100%',
          overflow: isMobile ? 'hidden' : 'auto'
        }}>
          {/* Only show LanguageSwitcher on non-/joinus routes */}
          {location.pathname !== '/joinus' && <LanguageSwitcher />}
          
          {/* Background Canvas - Only show for main route and not on mobile */}
          {location.pathname === '/' && !isMobile && (
            <div id="canvas-container" style={{ 
              position: 'fixed', 
              top: 0, 
              left: 0, 
              width: '100%', 
              height: '100%',
              zIndex: 0
            }}>
            <Canvas 
              id="bg-canvas"
              camera={{ position: [0, 2, 12], fov: fov }} 
                style={{ 
                  position: 'fixed', 
                  top: 0, 
                  left: 0, 
                  zIndex: 0 
                }}
              gl={{ 
                toneMapping: THREE.ACESFilmicToneMapping, 
                antialias: !isMobile,
                powerPreference: "high-performance",
                precision: isMobile ? "lowp" : "highp",
                depth: true,
                stencil: false,
              }}
              dpr={dpr}
              performance={{ min: 0.1 }}
              onCreated={({ scene }) => {
                console.log('[Debug] Canvas created, scene:', scene);
                  scene.background = new THREE.Color('#0a0a0a');
              }}
            >
              <ambientLight intensity={0.5} />
              <directionalLight intensity={3.0} position={[5, 10, 7]} />
              <CameraUpdater /> 
              <OrbitControls 
                enableZoom={true}
                enablePan={false}
                minDistance={minDistance} 
                maxDistance={maxDistance}
                minPolarAngle={Math.PI / 4} 
                maxPolarAngle={Math.PI / 1.8}
                  enableDamping={!isMobile}
                  rotateSpeed={isMobile ? 0.5 : 1}
              />
            </Canvas>
          </div>
          )}

          {/* Scroll Blocker Overlay - hide on mobile, /joinus, and /shop routes */}
          {!isMobile && location.pathname === '/' && (
            <div className="scroll-blocker-overlay" style={{ zIndex: 1 }}></div>
          )} 
          
          {/* Main Content Area */}
          <Routes>
            <Route path="/" element={
              <div className="content-wrapper" style={{ 
                position: 'relative', 
                zIndex: 2,
                width: '100%',
                overflow: isMobile ? 'hidden' : 'auto'
              }}> 
                <HeroSection />
                <AnimatedWaves />
                <MarineDilemmaSection />
                <ModelShowcaseSection />
                <UsecaseSection /> 
                <Market2Section />
                <InvestorOfferSection /> 
                <ContactSection />
              </div>
            } /> 
            <Route path="/joinus" element={
              <div 
                className="onepager-route"
                style={{ 
                  position: 'relative', 
                  zIndex: 2,
                  background: '#0a0a0a',
                  minHeight: isMobile ? '-webkit-fill-available' : '100vh',
                  width: '100%',
                  overflow: 'hidden',
                  margin: 0,
                  padding: 0
                }}>
                <OnePager />
              </div>
            } /> 
            <Route path="/imprint" element={
              <div style={{ position: 'relative', zIndex: 2 }}> 
                <ImprintPage />
              </div>
             } />
             <Route path="/privacy" element={
              <div style={{ position: 'relative', zIndex: 2 }}> 
                <PrivacyPolicyPage />
              </div>
             } />
             <Route path="/thank-you" element={
              <div style={{ position: 'relative', zIndex: 2 }}> 
                <ThankYouPage />
              </div>
             } />
             <Route path="/shop" element={<ProductList />} />
             <Route path="/shop/:handle" element={<ProductDetail />} />
             <Route path="/shop/cart" element={<Cart />} />
          </Routes>
          
          {/* Footer - hide on mobile and /joinus */}
          {location.pathname !== '/joinus' && !isMobile && <Footer />} 

          {/* Only show FloatingFAQButton on non-/joinus routes */}
          {location.pathname !== '/joinus' && (
            <FloatingFAQButton 
              onClick={() => setIsFAQModalOpen(true)} 
              style={{
                position: 'fixed',
                bottom: isMobile ? '20px' : '40px',
                right: isMobile ? '20px' : '40px',
                zIndex: 1000
              }}
            />
          )}

          {/* FAQ Modal */}
          <FAQModal 
            isOpen={isFAQModalOpen} 
            onClose={() => setIsFAQModalOpen(false)} 
          />
        </div>
      </>
    );
  }
}

export default App; 